/**
 * Routes.jsx
 *
 * Central routing configuration for the Angle! app.
 *
 * Architecture rules (DO NOT violate):
 *   - This file contains the ONLY <BrowserRouter> instance in the app.
 *   - App.jsx only renders <Routes /> — no routing components there.
 *   - All new routes must be added here.
 *
 * Page transitions:
 *   Every route is wrapped in <AnimatedRoute> which applies a subtle
 *   fade + slide animation via Framer Motion's AnimatePresence.
 *
 * Auth:
 *   Protected routes are wrapped in <ProtectedRoute> which redirects
 *   unauthenticated users to /auth, preserving the intended destination.
 */

import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import NotFound from 'pages/NotFound';
import RankingResultsDisplay from './pages/ranking-results-display';
import PhotoUploadInterface from './pages/photo-upload-interface';
import PurchaseCredits from './pages/purchase-credits';
import DashboardAnalysisHistory from './pages/dashboard-analysis-history';
import Checkout from './pages/checkout';
import FrequentlyAskedQuestions from './pages/frequently-asked-questions';
import Settings from './pages/settings';
import GeminiProfileInsights from './pages/gemini-profile-insights';
import AuthPage from './pages/auth';
import UserDashboard from './pages/user-dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { AuthProvider } from './contexts/AuthContext';
import LandingPage from './pages/landing';
import ConfirmEmailPage from './pages/confirm-email';
import ForgotPasswordPage from './pages/forgot-password';
import ResetPasswordPage from './pages/reset-password';
import OrderConfirmation from './pages/order-confirmation';
import TermsOfService from './pages/terms-of-service';
import PrivacyPolicy from './pages/privacy-policy';
import CreditPurchaseSuccess from './pages/credit-purchase-success';
import CookieConsentBanner from './components/CookieConsentBanner';
import BlogPage from './pages/blog';
import BlogPostPage from './pages/blog/[slug]/index';
import BlogAdminPage from './pages/blog-admin';
import BlogEditorPage from './pages/blog-admin/editor/index';
import GoogleAnalytics from './components/GoogleAnalytics';

// ─── Page transition animation variants ──────────────────────────────────────

/** Framer Motion variants shared by every page transition */
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.3, ease: 'easeIn' },
  },
};

/**
 * Wraps a page component with the shared enter/exit animation.
 *
 * @param {{ children: React.ReactNode }} props
 */
const AnimatedRoute = ({ children }) => (
  <motion.div
    variants={pageVariants}
    initial="initial"
    animate="animate"
    exit="exit"
  >
    {children}
  </motion.div>
);

/**
 * Renders all application routes inside AnimatePresence so that
 * exit animations play before the next page mounts.
 */
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <RouterRoutes location={location} key={location?.pathname}>
        {/* ── Public routes ─────────────────────────────────────────────── */}
        <Route path="/"                element={<AnimatedRoute><LandingPage /></AnimatedRoute>} />
        <Route path="/landing"         element={<AnimatedRoute><LandingPage /></AnimatedRoute>} />
        <Route path="/auth"            element={<AnimatedRoute><AuthPage /></AnimatedRoute>} />
        <Route path="/confirm-email"   element={<AnimatedRoute><ConfirmEmailPage /></AnimatedRoute>} />
        <Route path="/forgot-password" element={<AnimatedRoute><ForgotPasswordPage /></AnimatedRoute>} />
        <Route path="/reset-password"  element={<AnimatedRoute><ResetPasswordPage /></AnimatedRoute>} />
        <Route path="/purchase-credits" element={<AnimatedRoute><PurchaseCredits /></AnimatedRoute>} />
        <Route path="/checkout"        element={<AnimatedRoute><Checkout /></AnimatedRoute>} />
        <Route path="/order-confirmation" element={<AnimatedRoute><OrderConfirmation /></AnimatedRoute>} />
        <Route path="/credit-purchase-success" element={<AnimatedRoute><CreditPurchaseSuccess /></AnimatedRoute>} />
        <Route path="/terms-of-service"   element={<AnimatedRoute><TermsOfService /></AnimatedRoute>} />
        <Route path="/privacy-policy"     element={<AnimatedRoute><PrivacyPolicy /></AnimatedRoute>} />
        <Route path="/frequently-asked-questions" element={<AnimatedRoute><FrequentlyAskedQuestions /></AnimatedRoute>} />

        {/* ── Blog routes ────────────────────────────────────────────────── */}
        <Route path="/blog"              element={<AnimatedRoute><BlogPage /></AnimatedRoute>} />
        <Route path="/blog/:slug"        element={<AnimatedRoute><BlogPostPage /></AnimatedRoute>} />

        {/* ── Blog Admin routes (protected) ──────────────────────────────── */}
        <Route path="/blog-admin"        element={<AnimatedRoute><AdminRoute><BlogAdminPage /></AdminRoute></AnimatedRoute>} />
        <Route path="/blog-admin/editor" element={<AnimatedRoute><AdminRoute><BlogEditorPage /></AdminRoute></AnimatedRoute>} />
        <Route path="/blog-admin/editor/:id" element={<AnimatedRoute><AdminRoute><BlogEditorPage /></AdminRoute></AnimatedRoute>} />

        {/* ── Protected routes (require authentication) ─────────────────── */}
        <Route path="/user-dashboard"  element={<AnimatedRoute><ProtectedRoute><UserDashboard /></ProtectedRoute></AnimatedRoute>} />
        <Route path="/ranking-results-display" element={<AnimatedRoute><ProtectedRoute><RankingResultsDisplay /></ProtectedRoute></AnimatedRoute>} />
        <Route path="/photo-upload-interface"  element={<AnimatedRoute><ProtectedRoute><PhotoUploadInterface /></ProtectedRoute></AnimatedRoute>} />
        <Route path="/dashboard-analysis-history" element={<AnimatedRoute><ProtectedRoute><DashboardAnalysisHistory /></ProtectedRoute></AnimatedRoute>} />
        <Route path="/settings"        element={<AnimatedRoute><ProtectedRoute><Settings /></ProtectedRoute></AnimatedRoute>} />
        <Route path="/gemini-profile-insights" element={<AnimatedRoute><ProtectedRoute><GeminiProfileInsights /></ProtectedRoute></AnimatedRoute>} />

        {/* ── 404 fallback ──────────────────────────────────────────────── */}
        <Route path="*" element={<AnimatedRoute><NotFound /></AnimatedRoute>} />
      </RouterRoutes>
    </AnimatePresence>
  );
};

/**
 * Root router component.
 * Provides BrowserRouter, AuthProvider, ErrorBoundary, and ScrollToTop
 * to the entire application.
 */
const Routes = () => (
  <BrowserRouter>
    <AuthProvider>
      <ErrorBoundary>
        <GoogleAnalytics />
        <ScrollToTop />
        <AnimatedRoutes />
        <CookieConsentBanner />
      </ErrorBoundary>
    </AuthProvider>
  </BrowserRouter>
);

export default Routes;
